/**
 * Stock Solutions page — verbatim visual port from the standalone app.
 *
 * Preserves the original cyan/slate dark theme, header banner, footer
 * disclaimer, and two-column layout. AI Advisor panel removed per Josh.
 *
 * Phase 2.5: Added "Commercial Products" tab alongside existing "DIY Recipes".
 * Scoped under a `.reefapp-stock-solutions-scope` div to prevent the
 * cyan theme from leaking into the amber ReefApp shell, and vice versa.
 */
import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AquariumType, SolutionsCatalog, Solution } from '@reefapp/shared';
import { REEF_SOLUTIONS, FRESHWATER_SOLUTIONS } from '@reefapp/shared';

import { useServerBackedState } from '../hooks/useServerBackedState';
import { SolutionDetails } from '../stock-solutions/SolutionDetails';
import type { PrefillFromDeepLink } from '../stock-solutions/types';
import { FavoritesStrip } from '../stock-solutions/FavoritesStrip';
import { SupplementPicker } from '../stock-solutions/SupplementPicker';
import { ExportHistoryButton } from '../stock-solutions/ExportHistoryButton';
import { useStockSolutionsFavorites } from '../stock-solutions/useStockSolutionsFavorites';
import { WaterIcon } from '../stock-solutions/icons';
import { CommercialProductsList } from '../stock-solutions/CommercialProductsList';
import { CommercialProductDetail } from '../stock-solutions/CommercialProductDetail';
import type { CommercialProduct } from '@reefapp/shared';
import { COMMERCIAL_PRODUCTS_CATALOG } from '@reefapp/shared';

type TabId = 'diy' | 'commercial';

const TAB_STORAGE_KEY = 'reefapp:stock-solutions:tab';

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_TANK_VOLUME_KEY = 'reefapp:tank-volume-gal';

/** `/api/userdata` namespace holding the commercial-tab tank volume. */
export const TANK_VOLUME_NAMESPACE = 'tank-volume';

function readSessionStorageTab(): TabId {
  try {
    const v = sessionStorage.getItem(TAB_STORAGE_KEY);
    if (v === 'diy' || v === 'commercial') return v;
  } catch {
    /* noop */
  }
  return 'diy';
}

function writeSessionStorageTab(tab: TabId): void {
  try {
    sessionStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    /* noop */
  }
}

function readLegacyTankVolume(): number {
  try {
    const v = localStorage.getItem(LEGACY_TANK_VOLUME_KEY);
    if (v !== null) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch {
    /* noop */
  }
  return 0;
}

/** Normalize the server blob (number, or a legacy raw string). */
function asTankVolume(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function StockSolutionsPage() {
  const [aquariumType, setAquariumType] = useState<AquariumType>('reef');
  const [selectedSolutionKey, setSelectedSolutionKey] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabId>(readSessionStorageTab);
  const [selectedProduct, setSelectedProduct] = useState<CommercialProduct | null>(null);
  // Tank volume lives on the Pi (`tank-volume` namespace); the legacy
  // localStorage value seeds the default so it shows instantly.
  const [legacyTankVolume] = useState(readLegacyTankVolume);
  const { value: tankVolumeValue, setValue: setTankVolumeValue } =
    useServerBackedState<number | string>(TANK_VOLUME_NAMESPACE, legacyTankVolume);
  const tankVolume = asTankVolume(tankVolumeValue);
  const { favorites } = useStockSolutionsFavorites();

  const solutions: SolutionsCatalog = useMemo(
    () => (aquariumType === 'reef' ? REEF_SOLUTIONS : FRESHWATER_SOLUTIONS),
    [aquariumType],
  );

  const selectedSolution: Solution | null = useMemo(
    () => solutions[selectedSolutionKey] ?? null,
    [selectedSolutionKey, solutions],
  );

  const [searchParams] = useSearchParams();

  // Phase 3: read deep-link params from Test Kit Assistant
  useEffect(() => {
    const solutionParam = searchParams.get('solution');
    if (!solutionParam) return;

    // Try reef catalog first
    if (REEF_SOLUTIONS[solutionParam]) {
      setAquariumType('reef');
      setSelectedSolutionKey(solutionParam);
      return;
    }
    // Try freshwater catalog
    if (FRESHWATER_SOLUTIONS[solutionParam]) {
      setAquariumType('freshwater');
      setSelectedSolutionKey(solutionParam);
      return;
    }
    // Key not found in either — ignore gracefully
  }, []); // only on mount

  // Phase 4: read commercial product deep-link params (?product=<id>&delta=<n>&unit=<u>)
  useEffect(() => {
    const productParam = searchParams.get('product');
    if (!productParam) return;
    const found = COMMERCIAL_PRODUCTS_CATALOG.find((p: CommercialProduct) => p.id === productParam) ?? null;
    if (found) {
      setActiveTab('commercial');
      setSelectedProduct(found);
    }
  }, []); // only on mount

  const prefill: PrefillFromDeepLink | undefined = useMemo(() => {
    const current = searchParams.get('current');
    const target = searchParams.get('target');
    const unit = searchParams.get('unit');
    if (current || target || unit) {
      return {
        current: current ?? undefined,
        target: target ?? undefined,
        unit: unit ?? undefined,
      };
    }
    return undefined;
  }, [searchParams]);

  const handleTypeChange = (type: AquariumType) => {
    setAquariumType(type);
    setSelectedSolutionKey('');
  };

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    writeSessionStorageTab(tab);
    setSelectedProduct(null);
    setSelectedSolutionKey('');
  };

  const handleTankVolumeChange = (gal: number) => {
    setTankVolumeValue(gal);
  };

  const headerTitle =
    activeTab === 'commercial'
      ? 'Commercial Dosing Products'
      : 'DIY Aquarium Stock Solutions';

  const headerSubtitle =
    activeTab === 'commercial'
      ? 'Dosage calculators for popular aquarium supplements'
      : 'Your guide to Precision Aquarium Dosing using DIY Solutions';

  return (
    <div className="reefapp-stock-solutions-scope min-h-screen font-sans p-4 sm:p-6 lg:p-8 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        {/* Header banner */}
        <header className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-lg text-center mb-8 print:hidden">
          <div className="flex justify-center items-center gap-4">
            <WaterIcon className="h-12 w-12 text-cyan-400" />
            <h1 className="text-4xl sm:text-5xl font-bold text-cyan-300">
              {headerTitle}
            </h1>
          </div>
          <p className="mt-2 text-lg text-slate-300">{headerSubtitle}</p>
        </header>

        {/* Tab control */}
        <div className="flex justify-center mb-8 print:hidden">
          <div className="inline-flex bg-slate-800 border border-slate-700 rounded-lg p-1">
            <button
              onClick={() => handleTabChange('diy')}
              className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                activeTab === 'diy'
                  ? 'bg-cyan-600 text-white shadow'
                  : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              DIY Recipes
            </button>
            <button
              onClick={() => handleTabChange('commercial')}
              className={`px-6 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                activeTab === 'commercial'
                  ? 'bg-cyan-600 text-white shadow'
                  : 'text-slate-300 hover:bg-slate-700'
              }`}
            >
              Commercial Products
            </button>
          </div>
        </div>

        {/* DIY Recipes tab */}
        {activeTab === 'diy' && (
          <>
            <FavoritesStrip
              favorites={favorites}
              solutions={solutions}
              onSelect={setSelectedSolutionKey}
            />

            <main className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left sidebar — Controls */}
              <div className="lg:col-span-1 flex flex-col gap-8 print:hidden">
                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-lg">
                  <h2 className="text-2xl font-semibold mb-4 text-cyan-300">Controls</h2>

                  {/* Aquarium Type Selector */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Aquarium Type:
                    </label>
                    <div className="grid grid-cols-2 gap-2 bg-slate-900 p-1 rounded-lg">
                      <button
                        onClick={() => handleTypeChange('reef')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
                          aquariumType === 'reef'
                            ? 'bg-blue-600 text-white shadow'
                            : 'text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        Saltwater
                      </button>
                      <button
                        onClick={() => handleTypeChange('freshwater')}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors duration-200 ${
                          aquariumType === 'freshwater'
                            ? 'bg-green-600 text-white shadow'
                            : 'text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        Freshwater
                      </button>
                    </div>
                  </div>

                  {/* Solution Selector */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Dosing Supplement:
                    </label>
                    <SupplementPicker
                      solutions={solutions}
                      selectedKey={selectedSolutionKey}
                      onSelect={setSelectedSolutionKey}
                    />
                  </div>
                </div>

                <div className="print:hidden">
                  <ExportHistoryButton />
                </div>
              </div>

              {/* Right content area — Solution Details or Welcome message */}
              <div className="lg:col-span-2">
                {selectedSolution ? (
                  <SolutionDetails
                    solution={selectedSolution}
                    solutions={solutions}
                    prefill={prefill}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center min-h-[500px] print:hidden">
                    <WaterIcon className="h-24 w-24 text-slate-600 mb-4" />
                    <h3 className="text-2xl font-bold text-slate-300">Welcome!</h3>
                    <p className="text-slate-400 mt-2">
                      Please select an aquarium type and a supplement to get started.
                    </p>
                  </div>
                )}
              </div>
            </main>
          </>
        )}

        {/* Commercial Products tab */}
        {activeTab === 'commercial' && (
          <main className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left sidebar — Tank volume + product list */}
            <div className="lg:col-span-1 flex flex-col gap-6 print:hidden">
              {/* Tank volume input */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-lg">
                <label
                  htmlFor="tank-volume-input"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Tank Volume (US Gallons):
                </label>
                <input
                  id="tank-volume-input"
                  type="number"
                  min="0"
                  step="0.1"
                  value={tankVolume || ''}
                  onChange={(e) => handleTankVolumeChange(Number(e.target.value))}
                  placeholder="e.g. 50"
                  className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-slate-200 shadow-sm placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500"
                />
              </div>

              {/* Product list */}
              <CommercialProductsList
                products={COMMERCIAL_PRODUCTS_CATALOG}
                selectedProductId={selectedProduct?.id ?? null}
                onSelectProduct={setSelectedProduct}
              />
            </div>

            {/* Right content area — Product detail or welcome */}
            <div className="lg:col-span-2">
              {selectedProduct ? (
                <CommercialProductDetail
                  product={selectedProduct}
                  tankVolumeGal={tankVolume}
                  deltaParam={searchParams.get('delta') ?? undefined}
                  deltaUnit={searchParams.get('unit') ?? undefined}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center min-h-[500px] print:hidden">
                  <WaterIcon className="h-24 w-24 text-slate-600 mb-4" />
                  <h3 className="text-2xl font-bold text-slate-300">
                    Browse Commercial Products
                  </h3>
                  <p className="text-slate-400 mt-2">
                    Select a product from the list to view its dosing calculator.
                  </p>
                </div>
              )}
            </div>
          </main>
        )}

        {/* Footer disclaimer — identical to standalone */}
        <footer className="mt-12 text-center text-sm print:hidden">
          <div className="max-w-4xl mx-auto p-6 bg-slate-800 border border-slate-700 rounded-2xl">
            <h4 className="font-bold text-red-400 mb-2 text-base">
              You are using this calculator at your own risk.
            </h4>
            <p className="text-slate-300">
              The creator of this tool is not responsible for any personal injury or damage to your
              aquarium or its inhabitants. This tool assumes you have the required knowledge of
              aquarium chemistry and safety procedures before attempting to make any of these
              solutions.
            </p>
            <p className="mt-4 text-slate-400">&copy; 2024 by Josh Cole.</p>
          </div>
        </footer>
      </div>
    </div>
  );
}